import { useState } from 'react';
import { formApi } from '../services/api';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  CheckCircle2,
  Building2,
  User,
  MessageSquare,
  Briefcase,
  Package,
  ChevronDown
} from 'lucide-react';

const industryOptions = [
  { value: 'coal', label: '煤炭' },
  { value: 'chemical', label: '化工' },
  { value: 'cement', label: '水泥' },
  { value: 'mining', label: '矿山' },
  { value: 'food', label: '粮油食品' },
  { value: 'construction', label: '建筑' },
  { value: 'coking', label: '焦化' },
  { value: 'nonferrous', label: '有色金属' },
  { value: 'power', label: '电厂' },
  { value: 'other', label: '其他' },
];

const productOptions = [
  { value: 'shipping_code', label: '船运一码通管理' },
  { value: 'logistics_code', label: '企业智慧物流一码通管理' },
  { value: 'tms', label: '运输管理（TMS）' },
  { value: 'unmanned_weighing', label: '无人值守称重管理' },
  { value: 'smart_queue', label: '智能排队管理' },
  { value: 'quantitative_loading', label: '智能定量装车' },
  { value: 'crane_loading', label: '智能鹤管装车' },
  { value: 'hazard_parking', label: '危化品停车场管理' },
  { value: 'smart_dispatch', label: '智能调度' },
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    industry: '',
    industryOther: '',
    product: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const industryValue = formData.industry === 'other' 
        ? `其他(${formData.industryOther})` 
        : industryOptions.find(i => i.value === formData.industry)?.label || formData.industry;
      
      const productLabel = productOptions.find(p => p.value === formData.product)?.label || formData.product;
      
      const response = await formApi.submit({
        name: formData.name,
        company: formData.company,
        phone: formData.phone,
        email: formData.email,
        industry: industryValue,
        industry_other: formData.industry === 'other' ? formData.industryOther : undefined,
        product: productLabel,
        message: formData.message,
      });
      
      if (response.success) {
        setIsSubmitted(true);
        
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({ 
            name: '', 
            company: '', 
            phone: '', 
            email: '', 
            industry: '', 
            industryOther: '', 
            product: '', 
            message: '' 
          });
        }, 3000);
      } else {
        setSubmitError(response.message || '提交失败，请稍后重试');
      }
    } catch (err: any) {
      setSubmitError(err.message || '提交失败，请稍后重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="section-padding bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-4">
            <span className="w-2 h-2 bg-ocean-400 rounded-full" />
            <span className="text-ocean-300 text-sm font-medium">联系我们</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            开启智能化船运管理之旅
          </h2>
          <p className="text-lg text-slate-400">
            专业团队为您提供一对一咨询服务，量身定制解决方案
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <h3 className="text-xl font-bold text-white mb-6">联系方式</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-ocean-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-ocean-400" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm mb-1">服务热线</div>
                    <a href="tel:0371-65862867" className="text-white font-semibold text-lg hover:text-ocean-300 transition-colors">0371-65862867</a>
                    <a href="tel:13007504285" className="text-white font-semibold text-lg hover:text-ocean-300 transition-colors block">13007504285</a>
                    <div className="text-slate-500 text-sm">7x24小时服务</div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-ocean-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-ocean-400" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm mb-1">电子邮箱</div>
                    <div className="text-white font-semibold">bolesoft@qq.com</div>
                    <div className="text-slate-500 text-sm">sales@bole.com</div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-ocean-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-ocean-400" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm mb-1">公司地址</div>
                    <div className="text-white font-semibold">郑州市高新区瑞达路96号</div>
                    <div className="text-slate-500 text-sm">高新技术产业开发区</div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-ocean-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-ocean-400" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm mb-1">工作时间</div>
                    <div className="text-white font-semibold">周一至周六</div>
                    <div className="text-slate-500 text-sm">9:00 - 18:00</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-ocean-500/20 to-primary-600/20 rounded-2xl p-6 border border-ocean-500/30">
              <h4 className="text-lg font-semibold text-white mb-4">咨询服务包含</h4>
              <div className="space-y-3">
                {[
                  '免费需求分析',
                  '定制化方案设计',
                  '系统演示体验',
                  '实施周期评估',
                  '投资回报分析',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-ocean-400 flex-shrink-0" />
                    <span className="text-slate-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">预约咨询</h3>
              <p className="text-slate-500 mb-8">填写以下信息，我们的专业顾问将在24小时内与您联系</p>

              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h4 className="text-xl font-bold text-slate-900 mb-2">提交成功</h4>
                  <p className="text-slate-500">感谢您的咨询，我们将尽快与您联系</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {submitError && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                      {submitError}
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        联系人姓名 <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 outline-none transition-all"
                          placeholder="请输入姓名"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        公司名称 <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          required
                          className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 outline-none transition-all"
                          placeholder="请输入公司名称"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        联系电话 <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 outline-none transition-all"
                          placeholder="请输入联系电话"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        电子邮箱 <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 outline-none transition-all"
                          placeholder="请输入电子邮箱"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        所属行业 <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <select
                          name="industry"
                          value={formData.industry}
                          onChange={handleChange}
                          required
                          className="w-full pl-10 pr-10 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 outline-none transition-all appearance-none bg-white"
                        >
                          <option value="">请选择行业</option>
                          {industryOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        关注产品 <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <select
                          name="product"
                          value={formData.product}
                          onChange={handleChange}
                          required
                          className="w-full pl-10 pr-10 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 outline-none transition-all appearance-none bg-white"
                        >
                          <option value="">请选择产品</option>
                          {productOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                      </div>
                    </div>
                  </div>

                  {formData.industry === 'other' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        其他行业 <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="text"
                          name="industryOther"
                          value={formData.industryOther}
                          onChange={handleChange}
                          required={formData.industry === 'other'}
                          className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 outline-none transition-all"
                          placeholder="请输入所属行业"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      补充说明
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 outline-none transition-all resize-none"
                        placeholder="请描述您的其他需求或问题..."
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-gradient-to-r from-ocean-500 to-primary-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-ocean-500/30 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        提交中...
                      </>
                    ) : (
                      <>
                        提交咨询
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </button>

                  <p className="text-center text-sm text-slate-400">
                    提交即表示您同意我们的服务条款和隐私政策
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
